"use client";
import React from "react";
import {
  Bot,
  ChevronDown,
  ChevronRight,
  ChevronUp,
  CircleCheck,
  Clock,
  Folder,
  Loader,
  Plus,
  PlusCircle,
  Search,
  Trash2,
  X,
} from "lucide-react";
import {
  useEffect,
  useState,
  forwardRef,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { useAuthStore } from "../hooks/useAuth";

import RockstarLogo from "../assets/AI_District_Logo.png";

import {
  createConversationApi,
  deleteConversationApi,
} from "../../services/conversationApi";
import FeedbackModal from "./FeedbackModal";
import { PORTAL_URL } from "../../config";
import { useAuth } from "../context/AuthContext";

const LeftSidebar = forwardRef(
  (
    {
      onChatSelect,
      selectedChatId,
      chatTitles = {},
      chats,
      setChats,
      setAllChats,
      allCustomModels = [],
      customModelId,
      setSelectedChatId,
      setIsNewChat,
      chatId,
      onModelSelect,
      setMessages,
      clientBadgeLabel = null,
    },
    ref,
  ) => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { user } = useAuthStore();
    const { userData } = useAuth();
    const [searchQuery, setSearchQuery] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [selectedModel, setSelectedModel] = useState(null);
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [chatToDelete, setChatToDelete] = useState(null);
    const [isFeedbackModalOpen, setIsFeedbackModalOpen] = useState(false);
    const [showAllFolders, setShowAllFolders] = useState(false); // New state for folder list expansion
    const [expandedFolders, setExpandedFolders] = useState(new Set());
    const isManualSelection = useRef(false);

    // Filter functions
    const filteredModels = useMemo(() => {
      if (!searchQuery) return allCustomModels;

      const query = searchQuery.toLowerCase().trim();
      return allCustomModels.filter((model) => {
        const label = model.label?.toLowerCase() || "";
        const description = model.description?.toLowerCase() || "";
        const folder = model.folder?.toLowerCase() || "";
        const handle = model.modelHandle?.toLowerCase() || "";

        return (
          label.includes(query) ||
          description.includes(query) ||
          folder.includes(query) ||
          handle.includes(query)
        );
      });
    }, [searchQuery, allCustomModels]);

    const groupedModels = useMemo(
      () =>
        filteredModels.reduce((groups, model) => {
          // Handle different types of falsy values for folder
          const folder =
            model.folder && model.folder.trim() !== ""
              ? model.folder
              : "Uncategorized";

          if (!groups[folder]) {
            groups[folder] = [];
          }
          groups[folder].push(model);
          return groups;
        }, {}),
      [filteredModels],
    );

    const foldersToDisplay = useMemo(() => {
      const allFolders = Object.keys(groupedModels).sort((a, b) => {
        if (a === "ChatGPT") return -1;
        if (b === "ChatGPT") return 1;
        return a.localeCompare(b);
      });
      if (showAllFolders || allFolders.length <= 10) {
        return allFolders;
      }
      return allFolders.slice(0, 10);
    }, [groupedModels, showAllFolders]);

    // Handle folder expansion/collapse
    const toggleFolder = (folder) => {
      setExpandedFolders((prev) => {
        const newSet = new Set(prev);
        if (newSet.has(folder)) {
          newSet.delete(folder);
        } else {
          newSet.add(folder);
        }
        return newSet;
      });
    };

    const handleModelSelect = useCallback(
      (model) => {
        // Prevent sync effect from interfering with manual selection
        isManualSelection.current = true;
        setSelectedModel(model);
        if (onModelSelect) {
          onModelSelect(model);
        }
        // Reset flag after state updates complete
        setTimeout(() => {
          isManualSelection.current = false;
        }, 200);
      },
      [onModelSelect],
    );

    // Sync selectedModel with customModelId when it's explicitly set (e.g., from URL params)
    // Only sync if it's not a manual selection to avoid conflicts and glitchy behavior
    useEffect(() => {
      if (
        !isManualSelection.current &&
        customModelId?.id &&
        customModelId.id !== selectedModel?.id
      ) {
        setSelectedModel(customModelId);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [customModelId?.id, selectedModel?.id]);

    // Add this useEffect to update chat titles without full refresh
    useEffect(() => {
      // Update chat titles without refreshing the entire list
      if (Object.keys(chatTitles).length > 0) {
        setChats((prevChats) =>
          prevChats.map((chat) =>
            chatTitles[chat._id]
              ? { ...chat, title: chatTitles[chat._id] }
              : chat,
          ),
        );
      }
    }, [chatTitles, setChats]);

    // Filter chats for the selected model
    const filteredChat = useMemo(() => {
      if (!selectedModel?.id) return [];
      return chats.filter((chat) => chat.modelHandle?._id === selectedModel.id);
    }, [chats, selectedModel]);

    const toggleFolderList = () => {
      setShowAllFolders((prev) => !prev);
    };

    const remainingFoldersCount = useMemo(() => {
      return Math.max(0, Object.keys(groupedModels).length - 2);
    }, [groupedModels]);
    const handleDelete = async (chatId) => {
      if (!chatId) {
        toast.error("Missing chat ID");
        return;
      }
      setIsLoading(true);

      try {
        console.log("Attempting to delete chat:", chatId);
        const res = await deleteConversationApi(chatId);
        console.log("Delete API response:", res);

        // Remove chat from the list
        setChats((prev) => prev.filter((c) => c._id !== chatId));
        setAllChats((prev) => prev.filter((c) => c._id !== chatId));

        // If the deleted chat was selected, clear selection and go to new chat state
        if (selectedChatId?._id === chatId) {
          setSelectedChatId(null);
          setIsNewChat(true);
          onChatSelect(null, true); // Pass true for isNew
          // window.history.replaceState(null, "", "/dashboard");
          navigate("/dashboard", { replace: true });
        }

        toast.success("Chat deleted successfully", {
          position: "top-right",
          autoClose: 2000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
        });

        setDeleteModalOpen(false);
        setChatToDelete(null);
      } catch (error) {
        console.error("Error deleting chat:", error);
        console.error("Error details:", {
          message: error?.message,
          status: error?.status,
          response: error?.response?.data,
        });

        const errorMessage =
          error?.response?.data?.message ||
          error?.message ||
          "Failed to delete chat. Please try again.";

        toast.error(errorMessage, {
          position: "top-right",
          autoClose: 3000,
        });
      } finally {
        setIsLoading(false);
      }
    };

    const handleStartNewChat = async (modelId) => {
      // Validate that we have a valid model ID before creating a chat
      if (!modelId) {
        console.error("No custom model selected for new chat");
        toast.error("Please select a custom bot before creating a new chat");
        return;
      }

      try {
        // Create a new conversation first
        const conversationData = {
          conversationName: "New Chat",
          usedCustomModel: modelId,
          clientId: userData?.activeClientId,
        };

        const conversationResult =
          await createConversationApi(conversationData);

        if (conversationResult.success) {
          const newConversation = {
            _id: conversationResult.results.conversationId,
            title: conversationResult.results.conversationTitle,
            createdAt: new Date().toISOString(),
            modelHandle:
              conversationResult.results.customModelData ||
              allCustomModels[0]?.value,
          };
          // Add the new conversation to the sidebar
          setChats((prev) => [newConversation, ...prev]);
          setAllChats((prev) => [newConversation, ...prev]);
          setSelectedChatId(newConversation);

          onChatSelect(newConversation, false);

          // Update URL with chatId and botId
          const newSearchParams = new URLSearchParams(searchParams);
          newSearchParams.set("chatId", newConversation._id);
          if (modelId) {
            newSearchParams.set("botId", modelId);
          }
          navigate(
            `/dashboard/${newConversation._id}?${newSearchParams.toString()}`,
            { replace: true },
          );
        } else {
          throw new Error("Failed to create conversation");
        }
      } catch (error) {
        console.error("Failed to start new chat:", error);
        toast.error("Failed to start new chat");
      }
    };

    const openDeleteModal = (chatId) => {
      console.log("Opening delete modal for chat:", chatId);
      setChatToDelete(chatId);
      setDeleteModalOpen(true);
    };

    return (
      <div
        ref={ref}
        className="p-4 flex flex-col bg-white/90 rounded-2xl h-full lg:h-[calc(100vh-42px)] w-full overflow-hidden border border-gray-200/20 shadow-sm"
      >
        <div className="flex-shrink-0 border-b border-gray-200 pb-4">
          <div className="flex justify-between items-center mb-4 bg-gradient-to-br from-[#7612fa] to-[#fa12e3] gap-2 rounded-xl p-2 relative">
            <div
              className="flex items-center gap-3 cursor-pointer group relative hover:opacity-90 transition-opacity"
              onClick={() => {
                window.location.href = `${PORTAL_URL}/explore`;
              }}
            >
              <img
                src={RockstarLogo || "/placeholder.svg"}
                alt="Brand Story Logo"
                className="h-10 w-10"
                title="AI District Agent Portal"
              />
            </div>
            <h2 className="text-lg text-white font-semibold">Available Bots</h2>
            {user?.role === "admin" && (
              <button
                onClick={() => navigate("/admin-dashboard")}
                className="flex items-center gap-2 text-sm text-white bg-purple-700/80 hover:bg-purple-800/80 px-3 py-1.5 rounded-full hover:shadow-md disabled:opacity-50 transition-colors duration-200"
              >
                <PlusCircle size={16} />
                <span className="font-medium whitespace-nowrap">
                  Add New Bot
                </span>
              </button>
            )}
          </div>

          <div className="relative">
            <Search
              className="absolute  left-3 top-1/2 transform z-10 -translate-y-1/2 text-gray-800"
              size={16}
            />

            <input
              type="text"
              placeholder="Search bots..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent bg-white/80 backdrop-blur-sm"
            />

            {searchQuery.trim() !== "" && (
              <div
                onClick={() => setSearchQuery("")}
                className="absolute  right-3 top-1/2 transform -translate-y-1/2 border border-gray-300 p-1.5 rounded-full hover:bg-gray-200 duration-200 cursor-pointer"
              >
                <X className=" text-gray-400" size={16} />
              </div>
            )}
          </div>

          {clientBadgeLabel ? (
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <span className="text-xs font-semibold text-black sm:text-sm">
                Current client:
              </span>
              <span
                className={`inline-flex font-bold items-center gap-1.5 rounded-full border px-2.5 py-1 text-sm ${
                  userData?.isPrimaryUser
                    ? "border-[#006b44] bg-emerald-100 text-[#006b44]"
                    : "border-blue-400 bg-blue-100 text-blue-900"
                }`}
                title="Workspace used for saves in this tool"
              >
                <span
                  className={`h-2 w-2 shrink-0 rounded-full ${
                    userData?.isPrimaryUser ? "bg-[#006b44]" : "bg-blue-900"
                  }`}
                  aria-hidden
                />
                {clientBadgeLabel}
              </span>
            </div>
          ) : null}
        </div>

        {/* Delete Confirmation Modal */}
        {deleteModalOpen && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl p-6 max-w-sm w-full mx-4 transform transition-all duration-200 ease-out opacity-100 scale-100">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Delete Chat
              </h3>
              <p className="text-gray-600 mb-6">
                Are you sure you want to delete this chat? This action cannot be
                undone.
              </p>
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => {
                    setDeleteModalOpen(false);
                    setChatToDelete(null);
                  }}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800 border border-gray-300 rounded-lg hover:bg-gray-100"
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleDelete(chatToDelete)}
                  disabled={isLoading}
                  className="px-4 py-2 disabled:bg-red-400 disabled:cursor-not-allowed bg-red-500 text-white rounded-lg hover:bg-red-600"
                >
                  {isLoading ? (
                    <div className="flex items-center gap-1">
                      <span>Deleting </span>
                      <Loader size={18} className="animate-spin" />
                    </div>
                  ) : (
                    "Delete"
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
        {/* Content Area - Flexible height with proper scrolling */}
        <div className="flex-1 min-h-0  pr-2">
          {/* Models Tab */}

          <div className="space-y-3 relative  h-full">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-gray-800">Available Bots</h3>
            </div>

            {Object.keys(groupedModels).length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <Bot size={48} className="mx-auto mb-2 text-gray-300" />
                <p>No models found</p>
              </div>
            ) : (
              <div
                style={{ scrollbarGutter: "stable" }}
                className="space-y-2 flex-1 overflow-y-auto  h-[90%]"
              >
                {/* Display folders based on showAllFolders state */}
                {foldersToDisplay.map((folder) => {
                  const models = groupedModels[folder];
                  return (
                    <div key={folder} className="space-y-2">
                      {/* Folder Header */}
                      <div
                        onClick={() => toggleFolder(folder)}
                        className="flex items-center justify-between p-2 rounded-lg bg-gray-50 hover:bg-gray-100 cursor-pointer transition-colors"
                      >
                        <div className="flex items-center gap-2">
                          <Folder size={16} className="text-gray-600" />
                          <span className="text-sm font-medium text-gray-700">
                            {folder.charAt(0).toUpperCase() +
                              folder.slice(1).replaceAll("-", " ")}
                          </span>
                          <span className="text-xs text-gray-500 bg-gray-200 px-2 py-1 rounded-full">
                            {models.length}
                          </span>
                        </div>
                        {expandedFolders.has(folder) ? (
                          <ChevronDown size={16} className="text-gray-600" />
                        ) : (
                          <ChevronRight size={16} className="text-gray-600" />
                        )}
                      </div>

                      {/* Models in Folder */}
                      {expandedFolders.has(folder) && (
                        <div className="space-y-1 ml-4">
                          {models.map((model) => (
                            <div key={model.id} className="flex flex-col gap-1">
                              <div
                                onClick={() => {
                                  setMessages([]);
                                  handleModelSelect(model);
                                }}
                                className={`p-2  rounded-lg border transition-all cursor-pointer ${
                                  selectedModel?.id === model.id
                                    ? "bg-primary/10 border-primary/30"
                                    : "bg-white/60 backdrop-blur-sm border-gray-200 hover:border-primary/30"
                                }`}
                              >
                                <div className="flex items-center gap-2">
                                  <Bot size={16} className="text-primary" />
                                  <div className="flex-1">
                                    <h4 className="font-medium text-gray-800 text-sm">
                                      {model.label.charAt(0).toUpperCase() +
                                        model.label
                                          .slice(1)
                                          .replaceAll("-", " ")}
                                    </h4>
                                    {model.internetAccess && (
                                      <div className="flex items-center gap-1 mt-1">
                                        <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                                        <span className="text-xs text-green-600 font-medium">
                                          Internet Access
                                        </span>
                                      </div>
                                    )}
                                  </div>
                                  {selectedModel?.id === model.id && (
                                    <div className="flex items-center gap-2">
                                      <button
                                        onClick={() => {
                                          handleStartNewChat(model.id);
                                        }}
                                        className="flex items-center gap-1 text-xs bg-primary text-white  font-medium px-2 py-1 rounded-md hover:bg-primary/90 transition-colors shadow-sm"
                                        title="Start new chat"
                                      >
                                        <Plus size={12} />
                                        <span>New Chat</span>
                                      </button>
                                      <CircleCheck
                                        size={14}
                                        className="text-primary"
                                      />
                                    </div>
                                  )}
                                </div>
                              </div>
                              {/* Chat History for Selected Model - Only show when a chat is selected */}
                              {selectedModel?.id === model.id &&
                                filteredChat.length > 0 &&
                                selectedChatId && (
                                  <div className="flex flex-col mt-2 ml-2">
                                    <div className="flex items-center justify-between mb-2 px-2">
                                      <div className="text-xs font-semibold text-gray-600">
                                        Chat History ({filteredChat.length})
                                      </div>
                                      {filteredChat.length > 4 && (
                                        <button
                                          onClick={() => {
                                            const newSearchParams =
                                              new URLSearchParams(searchParams);
                                            newSearchParams.set(
                                              "botId",
                                              selectedModel.id,
                                            );
                                            newSearchParams.delete("chatId");
                                            navigate(
                                              `/dashboard?${newSearchParams.toString()}`,
                                              { replace: true },
                                            );
                                            setSelectedChatId(null);
                                            setIsNewChat(true);
                                            onChatSelect &&
                                              onChatSelect(null, true);
                                          }}
                                          className="text-xs text-primary hover:text-primary/80 font-medium px-2 py-1 rounded-md hover:bg-primary/10 transition-colors"
                                        >
                                          View All
                                        </button>
                                      )}
                                    </div>
                                    <div className="flex-1 overflow-y-auto scrollbar-hide min-h-0 p-1 pr-2 max-h-96">
                                      <div className="flex flex-col gap-2">
                                        {filteredChat
                                          .slice(0, 4)
                                          .map((chat) => {
                                            const chatId = chat._id;
                                            if (!chatId) {
                                              console.warn(
                                                "⚠️ Skipping chat with missing ID:",
                                                chat,
                                              );
                                              return null;
                                            }

                                            return (
                                              <div
                                                key={chatId}
                                                onClick={() =>
                                                  onChatSelect &&
                                                  onChatSelect(chat, false)
                                                }
                                                className={`p-3 rounded-xl border cursor-pointer transition-all ${
                                                  selectedChatId?._id === chatId
                                                    ? "bg-primary/10 border-primary/30"
                                                    : "bg-white/60 border-gray-200 hover:bg-gray-50 hover:border-primary/30"
                                                }`}
                                              >
                                                <div className="flex justify-between items-start mb-1">
                                                  <h3
                                                    className="text-sm font-medium text-gray-800 truncate flex-1"
                                                    title={chat.title}
                                                  >
                                                    {chat.title ||
                                                      "Untitled Chat"}
                                                  </h3>
                                                  <button
                                                    onClick={(e) => {
                                                      e.stopPropagation();
                                                      openDeleteModal(chatId);
                                                    }}
                                                    className="text-gray-400 hover:text-red-500 transition-colors p-1"
                                                    title="Delete conversation"
                                                  >
                                                    <Trash2 size={14} />
                                                  </button>
                                                </div>
                                                <div className="flex items-center gap-1 text-xs text-gray-500 mt-1">
                                                  <Clock size={12} />
                                                  <span>
                                                    {chat.createdAt
                                                      ? new Date(
                                                          chat.createdAt,
                                                        ).toLocaleDateString()
                                                      : "No date"}
                                                  </span>
                                                </div>
                                              </div>
                                            );
                                          })}
                                      </div>
                                    </div>
                                  </div>
                                )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}

                <FeedbackModal
                  isOpen={isFeedbackModalOpen}
                  onClose={() => setIsFeedbackModalOpen(false)}
                  conversationId={chatId}
                  modelHandle={selectedModel?.modelHandle}
                />
                {/* Show More/Less Button */}
                {Object.keys(groupedModels).length > 10 && (
                  <button
                    onClick={toggleFolderList}
                    className="w-full p-2 rounded-lg border border-gray-200 hover:border-primary/30 bg-white/60 hover:bg-primary/5 transition-all cursor-pointer flex items-center justify-center gap-2 text-sm text-gray-600 hover:text-primary"
                  >
                    {showAllFolders ? (
                      <>
                        <ChevronUp size={16} />
                        Show Less
                      </>
                    ) : (
                      <>
                        <ChevronDown size={16} />
                        Show More
                      </>
                    )}
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
        <div className="flex justify-center items-center w-full">
          <button
            onClick={() => setIsFeedbackModalOpen(true)}
            className="group mx-auto  px-3 py-1 bg-primary text-white text-sm rounded-lg hover:bg-primary/90 hover:shadow-lg hover:shadow-primary/25 transition-all duration-200 ease-out border border-primary/20 hover:border-primary/40"
          >
            <Plus
              size={14}
              className="inline mr-1 group-hover:rotate-90 transition-transform duration-200"
            />
            <span className="font-medium">Give feedback</span>
          </button>
        </div>
      </div>
    );
  },
);

export default LeftSidebar;
